import React, { useState } from "react";
import { ErrorMessage, Form, Formik } from "formik";
import TldrVideoInAction from "../TldrVideoInAction";
import { StyledTldrLabel } from "../../_components/styled/styles";
import AiTemplatesSelect from "../AiTemplatesSelect";
import TldrFormControl from "../../_components/common/TldrFormControl";
import { analyticsTrackEvent, toPascalCase } from "../../_utils/common";
import { AILanguages, AITones } from "../../_utils/constants";
import TldrSelect from "../../_components/common/TldrSelect";
import {
  Box,
  Button,
  Text,
  Tooltip,
  Flex,
  Divider,
  IconButton,
} from "@chakra-ui/react";
import { FaArrowCircleLeft, FaMagic, FaPencilAlt } from "react-icons/fa";
import { useTranslation } from "react-i18next";
import { useHistory } from "react-router-dom";
import { MAGICAL } from "../../_utils/routes";
import { AutoResizeTextarea, TldrBeatLoader } from "../../_components/common/statelessView";
import { FaArrowLeft } from "react-icons/fa";
import { primayColor } from "../../_components/styled/variable";

const serviceDescription =
  "We are design and collaboration platform for marketing teams";

const InputStyles = {
  position: "relative",
  flexWrap: "wrap",
  alignItems: "stretch",
  width: "100%",
};

const WorkspaceForm = ({
  prompt,
  lastRun,
  onComplete,
  onCompleteForm,
  onTemplateChange,
  props,
  showbreadCrumb,
  isMobileView,
}) => {
  const { t } = useTranslation(["ai"]);
  const { document } = props;
  const { generatingResults } = document;
  const history = useHistory();
  const [titleCounter, setTitleCounter] = useState(120);
  const [descriptionCounter, setDescriptionCounter] = useState(600);

  return (
    <Formik
      enableReinitialize={true}
      initialValues={{
        title: lastRun.title,
        description: lastRun.description,
        keywords: lastRun.keywords,
        output: lastRun.output,
        tone: lastRun.tone ?? "",
      }}
      validate={(values) => {
        const errors = {};
        if (prompt.content.title) {
          if (!values.title) {
            errors.title = t("more_context_msg");
          } else if (values.title.length > 120) {
            errors.title = t("no_more_char_120");
          }
        }
        if (prompt.content.description) {
          if (!values.description || values.description.length < 10) {
            errors.description = t("more_context_msg");
          } else if (values.description.length > 600) {
            errors.description = t("no_more_char_600");
          }
        }

        if (prompt.content.keywords) {
          if (!values.keywords || values.keywords.length < 1) {
            errors.keywords = t("more_context_msg");
          } else if (values.keywords.length > 600) {
            errors.keywords = t("no_more_char_600");
          }
        }

        return errors;
      }}
      onSubmit={(values, { setSubmitting, setErrors }) => {
        let keywods = values["keywords"];
        if (!Array.isArray(keywods)) {
          values["keywords"] = keywods.split(",");
        }
        analyticsTrackEvent("aiGenerate", { language: values.output });
        onComplete(prompt.id, values);
      }}
    >
      {({ isSubmitting, handleChange, setFieldValue, isValid, values }) => (
        <>
          {!lastRun?.id && (
            <>
              <Flex
                direction={["column", "column", "row", "row"]}
                align={["left", "left", "center", "center"]}
                justify="space-between"
              >
                {!props.backbtn ? (
                  ""
                ) : (
                  <Tooltip
                    hasArrow
                    placement="bottom"
                    label={t("back_to_tem")}
                    shouldWrapChildren
                  >
                    <FaArrowCircleLeft
                      size={24}
                      className="back-btn"
                      onClick={() => {
                        history.push({
                          pathname: encodeURI(MAGICAL),
                          state: {
                            tab: "goals",
                          },
                        });
                        onCompleteForm("welcome");
                      }}
                    />
                  </Tooltip>
                )}
                <Flex direction="row" align="top" width="70%">
                  <IconButton
                    icon={<FaArrowLeft size="1rem" />}
                    mr="3"
                    borderRadius="full"
                    onClick={() => history.goBack()}
                  />

                  <Box textAlign="left">
                    <Text
                      color="gray.100"
                      fontSize="1.2rem"
                      fontWeight="500"
                      lineHeight="28px"
                      mr={["0.5", "0"]}
                    >
                      {prompt.content.heroText}
                    </Text>

                    <Text
                      p="0"
                      fontSize={!isMobileView ? "1rem" : "0.875rem"}
                      color="gray.300"
                    >
                      {prompt.content.heroDescription}
                    </Text>
                    {prompt.content.videoInAction && isMobileView && (
                      <Box mt={2}>
                        <TldrVideoInAction
                          title={prompt.title}
                          videoInAction={prompt.content.videoInAction}
                          color="white"
                          variant="secondary"
                        />
                      </Box>
                    )}
                  </Box>
                </Flex>

                {prompt.content.videoInAction && !isMobileView && (
                  <>
                    <TldrVideoInAction
                      title={prompt.title}
                      videoInAction={prompt.content.videoInAction}
                      color="white"
                      variant="secondary"
                    />
                  </>
                )}
              </Flex>

              <Divider mt={4} />
            </>
          )}

          <Form>
            {lastRun.id && (
              <Flex sx={InputStyles} my={4}>
                <div className="tldr-flex-grow">
                  <Box as={StyledTldrLabel} className="tldr-flex-grow" mb={1}>
                    {t("select_template")}
                  </Box>
                  <AiTemplatesSelect
                    onSelect={onTemplateChange}
                  ></AiTemplatesSelect>
                </div>
              </Flex>
            )}

            <Flex sx={InputStyles} my={4}>
              {prompt && prompt?.content?.tone && (
                <Box className="tldr-flex-grow" mr={1}>
                  <Box as={StyledTldrLabel} className="tldr-flex-grow" mb={1}>
                    {t("select_tone")}
                  </Box>
                  <TldrSelect
                    options={AITones}
                    defaultOption={lastRun.tone}
                    onSelect={(output) => {
                      setFieldValue("tone", output.value);
                    }}
                    value={values.tone}
                  ></TldrSelect>
                </Box>
              )}

              <div className="tldr-flex-grow">
                <Box as={StyledTldrLabel} className="tldr-flex-grow" mb={1}>
                  {t("output_lang")}
                </Box>
                <TldrSelect
                  options={AILanguages}
                  defaultOption={lastRun.output}
                  onSelect={(output) => {
                    setFieldValue("output", output.value);
                  }}
                  value={values.output}
                ></TldrSelect>
              </div>
            </Flex>

            {prompt && prompt?.content?.title && (
              <>
                <Flex
                  position="relative"
                  wrap="wrap"
                  width="100%"
                  alignItems="stretch"
                >
                  <Box as={StyledTldrLabel} className="tldr-flex-grow" mb={1}>
                    {prompt?.content?.title}
                  </Box>
                  <Box as={StyledTldrLabel} mb={1}>
                    {titleCounter}/120
                  </Box>
                  <AutoResizeTextarea
                    fontSize="1rem"
                    size="md"
                    type="text"
                    placeholder={
                      lastRun.title
                        ? lastRun.title
                        : prompt.content.title_placeholder
                    }
                    aria-describedby="basic-addon1"
                    name="title"
                    onChange={(e) => {
                      setTitleCounter(e.target.value.length);
                      handleChange(e);
                    }}
                  />
                </Flex>
                <ErrorMessage name="title" component="div" className="error" />
              </>
            )}

            {prompt.content.description && (
              <Flex sx={InputStyles} my={4}>
                <Box as={StyledTldrLabel} className="tldr-flex-grow" mb={1}>
                  {prompt.content.description}
                </Box>
                <Box as={StyledTldrLabel} mb={1}>
                  {descriptionCounter}/600
                </Box>
                <TldrFormControl
                  onChange={(e) => {
                    setDescriptionCounter(e.target.value.length);
                    handleChange(e);
                  }}
                  placeholder={
                    prompt.content.description_ph
                      ? prompt.content.description_ph
                      : serviceDescription
                  }
                  control="textarea"
                  label="description"
                  name="description"
                ></TldrFormControl>
              </Flex>
            )}

            {prompt.content.keywords && (
              <>
                <Flex sx={InputStyles} my={4}>
                  <Box as={StyledTldrLabel} mb={1}>
                    {prompt.content.keywords}
                  </Box>
                  <TldrFormControl
                    placeholder={
                      lastRun?.keywords
                        ? lastRun.keywords
                        : prompt.content.keyword_ph
                        ? prompt.content.keyword_ph
                        : t("brand_marketing_placeholder")
                    }
                    control="textarea"
                    label="Keywords"
                    name="keywords"
                  ></TldrFormControl>
                </Flex>
              </>
            )}
            <Divider mt={4} />
            <Flex
              className="form-button ai-workspace-form-btn-container"
              mt={4}
              sx={InputStyles}
            >
              <Button
                leftIcon={<FaMagic icon="magic" color="black"></FaMagic>}
                isLoading={generatingResults}
                type="submit"
                variant="solid"
                disabled={generatingResults || !isValid}
                spinner={<TldrBeatLoader bgColor={primayColor} />}
              >
                {t("generate_btn")}
              </Button>
            </Flex>
          </Form>
        </>
      )}
    </Formik>
  );
};

export default WorkspaceForm;
